import { ComponentRef, ApplicationRef, EnvironmentInjector, createComponent, inject } from '@angular/core';
import { BoundingBox } from '@newroom-connect/library/types';
import { LoggingService } from '@newroom-connect/library/services';
import { ICornerCoordinates, IDimensions } from '@newroom-connect/library/interfaces';
import { MatrixHelper, ITransform, TransformCoordinateHelper } from '@newroom-connect/library/helpers';

import { CornerDotsOverlayComponent, ICornersTransformEvent } from '../components/corner-dots-overlay/corner-dots-overlay.component';

export interface IBoundingBoxEvent {
  hotspotId: string;
  boundingBox: BoundingBox;
}

/**
 * Transform Overlay Manager.
 *
 * Handles corner dots overlay creation, positioning, and transform events.
 */
export class TransformOverlay {
  private readonly appRef = inject(ApplicationRef);
  private readonly envInjector = inject(EnvironmentInjector);
  private readonly logger = inject(LoggingService);

  private boundingBox: BoundingBox | null = null;
  private hotspotId: string | null = null;

  private overlayComponent: ComponentRef<CornerDotsOverlayComponent> | null = null;

  /**
   * Create transform overlay with corner dots component.
   *
   * @param hotspotId - The unique identifier for the hotspot.
   * @param boundingBox - The bounding box data.
   * @param mainHotspotElement
   * @param boundingBoxChanged - Callback for bounding box changes.
   * @param transformStart - Callback for transform start.
   * @param transformEnd - Callback for transform end.
   *
   * @returns The created container element or null if there is an error.
   */
  public createTransformOverlayComponent(
    hotspotId: string,
    boundingBox: BoundingBox,
    mainHotspotElement: HTMLElement,
    boundingBoxChanged: (event: IBoundingBoxEvent) => void,
    transformStart: () => void,
    transformEnd: () => void
  ): HTMLElement {
    const baseElement = this.createTransformOverlayBaseElement(mainHotspotElement);

    try {
      if (!mainHotspotElement) {
        return baseElement;
      }

      // Store the bounding box and the callback for bounding box changes.
      this.boundingBox = boundingBox;
      this.hotspotId = hotspotId;

      const width = mainHotspotElement.offsetWidth || 200;
      const height = mainHotspotElement.offsetHeight || 150;

      // Create Angular component
      const compRef = createComponent(CornerDotsOverlayComponent, { environmentInjector: this.envInjector });

      // Configure component
      compRef.instance.width = width;
      compRef.instance.height = height;
      compRef.instance.hotspotElement = mainHotspotElement;

      // Initialize with saved corners if available
      const initialCorners = boundingBox.perspective?.cornerPositions;

      if (initialCorners) {
        // Adjust to current overlay size before applying
        const currentDimensions: IDimensions = { width, height };

        const adjusted = TransformCoordinateHelper.adjustCornersToCurrentSize(
          initialCorners,
          currentDimensions,
          boundingBox.perspective?.cornerPositionsNormalized,
          boundingBox.perspective?.referenceSize
        );

        compRef.instance.initialCorners = adjusted;
      }

      // Wire transform events
      this.setupTransformEvents(compRef, boundingBoxChanged, transformStart, transformEnd);

      // Mount component
      this.appRef.attachView(compRef.hostView);

      // Store the overlay component for future reference.
      this.overlayComponent = compRef;

      // Append the overlay component to the base element.
      baseElement.appendChild(compRef.location.nativeElement);

      // Apply saved transform after component is mounted and DOM is ready
      if (initialCorners) {
        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(() => {
          try {
            // Always recompute from saved corners for current size
            const currentDimensions: IDimensions = { width, height };

            const adjusted = TransformCoordinateHelper.adjustCornersToCurrentSize(
              initialCorners,
              currentDimensions,
              boundingBox.perspective?.cornerPositionsNormalized,
              boundingBox.perspective?.referenceSize
            );

            const updatedBoundingBox = this.applySavedCornersTransform(adjusted);

            if (updatedBoundingBox) {
              this.boundingBox = updatedBoundingBox;

              // Emit only perspective updates to avoid overwriting current spherical position
              const patchBoundingBox: BoundingBox = {
                width: this.boundingBox.width,
                height: this.boundingBox.height,
                top: this.boundingBox.top,
                left: this.boundingBox.left,
                perspective: {
                  ...this.boundingBox.perspective
                }
              };

              boundingBoxChanged({ hotspotId, boundingBox: patchBoundingBox });
            }
          } catch (error) {
            this.logger.warn('Failed to recompute matrix from saved corners on mount', error as Error);
          }
        }, 0);
      }

      return baseElement;
    } catch (error) {
      this.logger.error('Failed to create transform overlay', error as Error);
      return baseElement;
    }
  }

  /**
   * Destroy the transform overlay and clean up resources.
   */
  public destroy(): void {
    if (this.overlayComponent) {
      this.appRef.detachView(this.overlayComponent.hostView);
      this.overlayComponent.destroy();

      this.overlayComponent = null;
    }

    this.hotspotId = null;
    this.boundingBox = null;
  }

  /**
   * Show the transform overlay with corner dots.
   */
  public show(): void {
    if (this.overlayComponent && this.overlayComponent.location.nativeElement) {
      // Show the base container
      this.overlayComponent.location.nativeElement.style.display = 'block';
    }
  }

  /**
   * Hide the transform overlay and corner dots.
   */
  public hide(): void {
    if (this.overlayComponent && this.overlayComponent.location.nativeElement) {
      this.overlayComponent.location.nativeElement.style.display = 'none';
    }
  }

  /**
   * Recompute and apply transform matrix from saved corners for current size.
   *
   * @param newCorners
   *
   * @returns The updated bounding box or null if there is an error.
   */
  public applySavedCornersTransform(newCorners: ICornerCoordinates): BoundingBox | null {
    if (!this.overlayComponent || !this.boundingBox || !this.hotspotId) {
      return null;
    }

    if (!newCorners) {
      return null;
    }

    const dimensions: IDimensions = {
      width: this.overlayComponent.instance.width,
      height: this.overlayComponent.instance.height
    };

    const cornersInPixels = TransformCoordinateHelper.adjustCornersToCurrentSize(
      newCorners,
      dimensions,
      this.boundingBox.perspective?.cornerPositionsNormalized,
      this.boundingBox.perspective?.referenceSize
    );

    try {
      const transformObject: ITransform = {
        element: this.overlayComponent.instance.hotspotElement,
        style: this.overlayComponent.instance.hotspotElement.style.transform,
        computedStyle: getComputedStyle(this.overlayComponent.instance.hotspotElement),
        width: dimensions.width,
        height: dimensions.height,
        useBackfacing: false,
        topLeft: cornersInPixels.topLeft,
        topRight: cornersInPixels.topRight,
        bottomLeft: cornersInPixels.bottomLeft,
        bottomRight: cornersInPixels.bottomRight
      };

      if (MatrixHelper.hasTransformError(transformObject)) {
        return null;
      }

      const result = MatrixHelper.transform(transformObject);

      // Persist updated matrix (so future loads don't flash old one)
      return {
        ...this.boundingBox,
        perspective: {
          ...this.boundingBox.perspective,
          transformMatrix: result.style
        }
      };
    } catch (error) {
      this.logger.warn('Failed to apply saved corners transform', error as Error);
      return null;
    }
  }

  /**
   * Clean up the transform overlay and remove the overlay component.
   *
   */
  public cleanupOverlay(): void {
    if (this.overlayComponent) {
      this.appRef.detachView(this.overlayComponent.hostView);
      this.overlayComponent.destroy();
      this.overlayComponent = null;
    }
  }

  /**
   * Setup transform event handlers for the corner dots component.
   *
   * @param compRef - Component reference to the corner dots overlay.
   * @param onBoundingBoxChanged - Callback for bounding box changes.
   * @param transformStart - Callback for transform start.
   * @param transformEnd - Callback for transform end.
   */
  private setupTransformEvents(compRef: ComponentRef<CornerDotsOverlayComponent>, onBoundingBoxChanged: (event: IBoundingBoxEvent) => void, transformStart: () => void, transformEnd: () => void): void {
    // Transform matrix changes
    compRef.instance.transformChanged.subscribe((matrix: string) => {
      try {
        if (!compRef || !compRef.instance || !compRef.instance.hotspotElement || !this.boundingBox || !this.hotspotId) {
          return;
        }

        this.boundingBox.perspective = {
          ...this.boundingBox.perspective,
          transformMatrix: matrix
        };

        // Emit only perspective updates to avoid overwriting current spherical position
        const patchBoundingBox: BoundingBox = {
          width: this.boundingBox.width,
          height: this.boundingBox.height,
          top: this.boundingBox.top,
          left: this.boundingBox.left,
          perspective: {
            ...this.boundingBox.perspective
          }
        };

        onBoundingBoxChanged({ hotspotId: this.hotspotId, boundingBox: patchBoundingBox });
      } catch (error) {
        this.logger.error('Failed to handle transform change', error as Error);
      }
    });

    // Transform metrics (corner positions)
    compRef.instance.transformMetricsChanged.subscribe((payload: ICornersTransformEvent): void => {
      try {
        if (!this.hotspotId || !this.boundingBox || !this.overlayComponent) {
          return;
        }

        // Get current overlay dimensions
        const currentDimensions: IDimensions = {
          width: this.overlayComponent.instance.width,
          height: this.overlayComponent.instance.height
        };

        // Convert corner positions to percentages for normalized storage
        const cornerPercentages = TransformCoordinateHelper.cornersToPercentages(
          payload.corners,
          currentDimensions
        );

        // Update bounding box with complete perspective data
        // Note: payload.matrix already contains the correct transform matrix
        this.boundingBox = {
          ...this.boundingBox,
          perspective: {
            ...this.boundingBox.perspective,
            // Use the matrix from the payload (already calculated correctly)
            transformMatrix: payload.matrix,
            // Save corner positions as percentages
            cornerPositions: cornerPercentages,
            // Mark as normalized (stored as percentages)
            cornerPositionsNormalized: true,
            // Save reference size for future scaling
            referenceSize: currentDimensions
          }
        };

        // Emit only perspective updates to avoid overwriting current spherical position
        const patchBoundingBox: BoundingBox = {
          width: this.boundingBox.width,
          height: this.boundingBox.height,
          top: this.boundingBox.top,
          left: this.boundingBox.left,
          perspective: {
            ...this.boundingBox.perspective
          }
        };

        onBoundingBoxChanged({ hotspotId: this.hotspotId, boundingBox: patchBoundingBox });
      } catch (error) {
        this.logger.error('Failed to handle transform metrics', error as Error);
      }
    });

    // Transform start/end (disable/enable controls)
    compRef.instance.transformStart.subscribe((): void => {
      transformStart();
    });

    compRef.instance.transformEnd.subscribe((corners: ICornerCoordinates): void => {
      if (!this.hotspotId || !this.boundingBox || !this.overlayComponent) {
        return;
      }

      // Get current overlay dimensions
      const currentDimensions: IDimensions = {
        width: this.overlayComponent.instance.width,
        height: this.overlayComponent.instance.height
      };

      // Convert corner positions to percentages for normalized storage
      const cornerPercentages = TransformCoordinateHelper.cornersToPercentages(
        corners,
        currentDimensions
      );

      // Calculate the final transform matrix from the current corner positions
      try {
        const transformObject: ITransform = {
          element: this.overlayComponent.instance.hotspotElement,
          style: this.overlayComponent.instance.hotspotElement.style.transform,
          computedStyle: getComputedStyle(this.overlayComponent.instance.hotspotElement),
          width: currentDimensions.width,
          height: currentDimensions.height,
          useBackfacing: false,
          topLeft: corners.topLeft,
          topRight: corners.topRight,
          bottomLeft: corners.bottomLeft,
          bottomRight: corners.bottomRight
        };

        if (!MatrixHelper.hasTransformError(transformObject)) {
          const result = MatrixHelper.transform(transformObject);

          // Update bounding box with complete perspective data
          this.boundingBox = {
            ...this.boundingBox,
            perspective: {
              ...this.boundingBox.perspective,
              // Use the calculated transform matrix
              transformMatrix: result.style,
              // Save corner positions as percentages
              cornerPositions: cornerPercentages,
              // Mark as normalized (stored as percentages)
              cornerPositionsNormalized: true,
              // Save reference size for future scaling
              referenceSize: currentDimensions
            }
          };

          // Emit only perspective updates to avoid overwriting current spherical position
          const patchBoundingBox: BoundingBox = {
            width: this.boundingBox.width,
            height: this.boundingBox.height,
            top: this.boundingBox.top,
            left: this.boundingBox.left,
            perspective: {
              ...this.boundingBox.perspective
            }
          };

          onBoundingBoxChanged({ hotspotId: this.hotspotId, boundingBox: patchBoundingBox });
        }
      } catch (error) {
        this.logger.error('Failed to calculate final transform matrix', error as Error);
      }

      transformEnd();
    });
  }

  /**
   * Create the transform overlay hotspot to host corner dots UI.
   *
   * @param hotspotElement - The base hotspot element for sizing reference.
   *
   * @returns The created container element.
   */
  private createTransformOverlayBaseElement(hotspotElement: HTMLElement): HTMLElement {
    // Calculate dimensions
    const width = hotspotElement?.offsetWidth || 200;
    const height = hotspotElement?.offsetHeight || 150;

    // Create container for overlay UI
    const container = document.createElement('div');

    container.className = 'nrc-hotspot-transform-overlay';
    container.style.position = 'relative';
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;
    container.style.pointerEvents = 'none'; // Allow clicks to pass through to underlying elements
    // Remove z-index - will be handled by Marzipano layering system

    // Start hidden - visibility will be controlled by show/hide methods
    container.style.display = 'none';

    return container;
  }

  /**
   * Update overlay size when hotspot element size changes and recompute matrix from saved corners.
   *
   * @param width New width.
   * @param height New height.
   * @param onUpdated Optional callback with updated bounding box.
   */
  public updateSize(width: number, height: number, onUpdated?: (updated: BoundingBox | null) => void): void {
    if (!this.overlayComponent) {
      onUpdated?.(null);
      return;
    }

    try {
      // Update base element size
      const baseElement = this.overlayComponent.location.nativeElement.parentElement as HTMLElement | null;

      if (baseElement) {
        baseElement.style.width = `${width}px`;
        baseElement.style.height = `${height}px`;
      }

      // Update component instance dims
      this.overlayComponent.instance.width = width;
      this.overlayComponent.instance.height = height;
      this.overlayComponent.changeDetectorRef.detectChanges();

      // Recompute matrix from saved corners if we have them
      if (this.boundingBox?.perspective?.cornerPositions) {
        const adjusted = TransformCoordinateHelper.adjustCornersToCurrentSize(
          this.boundingBox.perspective.cornerPositions,
          { width, height },
          this.boundingBox.perspective.cornerPositionsNormalized,
          this.boundingBox.perspective.referenceSize
        );

        const updated = this.applySavedCornersTransform(adjusted);

        if (updated) {
          this.boundingBox = updated;
        }

        onUpdated?.(updated ?? null);
      } else {
        onUpdated?.(null);
      }
    } catch {
      onUpdated?.(null);
    }
  }
}
